import { UPCOMING } from './constants';
import {
  initialSelfCareActivities,
  initialConditions,
  initialRecommendedExercises,
  initialOptionalExercises,
  initialCharityActivities
} from './accountability-util';

export const checkForGeneralChanges = (oldProgramme, newProgramme) => {
  if (oldProgramme.status === UPCOMING && !oldProgramme.externalReference) {
    if (newProgramme.name) return true;
    if (newProgramme.description) return true;
    if (newProgramme.toDate) return true;
    if (newProgramme.fromDate) return true;
    if (!newProgramme.isNutrition) return true;
    if (!newProgramme.isMovement) return true;
    if (!newProgramme.isSleep) return true;
    if (!newProgramme.isCondition) return true;
    if (newProgramme.leaderboardCollectFromDate) return true;
    if (newProgramme.hasRegistrationRequirements) return true;

    return false;
  }

  if (oldProgramme.name !== newProgramme.name) return true;
  if (oldProgramme.description !== newProgramme.description) return true;
  if (oldProgramme.toDate !== newProgramme.toDate?.toJSON()) return true;
  if (oldProgramme.fromDate !== newProgramme.fromDate?.toJSON()) return true;
  if (oldProgramme.isNutrition !== newProgramme.isNutrition) return true;
  if (oldProgramme.isMovement !== newProgramme.isMovement) return true;
  if (oldProgramme.isSleep !== newProgramme.isSleep) return true;
  if (oldProgramme.isCondition !== newProgramme.isCondition) return true;
  if (oldProgramme.leaderboardCollectFromDate !== newProgramme.leaderboardCollectFromDate?.toJSON()) return true;
  if (oldProgramme.hasRegistrationRequirements !== newProgramme.hasRegistrationRequirements) return true;

  const oldRegistrationRequirement = oldProgramme.registrationRequirement;
  const newRegistrationRequirement = newProgramme.registrationRequirement;

  if (!oldRegistrationRequirement && newRegistrationRequirement) return true;
  if (oldRegistrationRequirement && !newRegistrationRequirement) return true;
  if (oldRegistrationRequirement && newRegistrationRequirement) {
    if (oldRegistrationRequirement.payfastMerchantId !== newRegistrationRequirement.payfastMerchantId) return true;
    if (oldRegistrationRequirement.payfastMerchantKey !== newRegistrationRequirement.payfastMerchantKey) return true;
    if (oldRegistrationRequirement.programmePrice !== newRegistrationRequirement.programmePrice) return true;

    const oldRequiredFields = oldRegistrationRequirement.requiredRegistrationFields;
    const newRequiredFields = newRegistrationRequirement.requiredRegistrationFields;

    if (oldRequiredFields && newRequiredFields) {
      if (oldRequiredFields.length !== newRequiredFields.length) return true;
      for (let i = 0; i < oldRequiredFields.length; i++) {
        if (oldRequiredFields[i] !== newRequiredFields[i]) return true;
      }
    }
  }

  return false;
};

export const checkForAccountabilityChanges = (
  accountabilityTemplate,
  weightings,
  measurement,
  conditions,
  recommendedExercises,
  optionalExercises,
  selfCareActivities,
  sessions,
  charityTemplates
) => {
  let isChanged = checkForWeightingChanges(accountabilityTemplate, weightings);
  if (!isChanged) isChanged = checkForMeasurementChanges(accountabilityTemplate, measurement);
  if (!isChanged) isChanged = checkForConditionChanges(accountabilityTemplate, conditions);
  if (!isChanged) isChanged = checkForRecommendedExercisesChanges(accountabilityTemplate, recommendedExercises);
  if (!isChanged) isChanged = checkForOptionalExercisesChanges(accountabilityTemplate, optionalExercises);
  if (!isChanged) isChanged = checkForSelfCareActivitiesChanges(accountabilityTemplate, selfCareActivities);
  if (!isChanged) isChanged = checkForSessionChanges(accountabilityTemplate, sessions);
  if (!isChanged) isChanged = checkForCharityChanges(accountabilityTemplate, charityTemplates);

  return isChanged;
};

export const checkForSupportChanges = (oldSupport, newSupport) => {
  if (!oldSupport && newSupport) {
    if (newSupport.firstName) return true;
    if (newSupport.lastName) return true;
    if (newSupport.email) return true;
    if (newSupport.phone) return true;
    if (newSupport.frequentlyAskedQuestionsString) return true;
  }

  if (oldSupport && newSupport) {
    if (oldSupport.firstName !== newSupport.firstName) return true;
    if (oldSupport.lastName !== newSupport.lastName) return true;
    if (oldSupport.email !== newSupport.email) return true;
    if (oldSupport.phone !== newSupport.phone) return true;
    if (oldSupport.frequentlyAskedQuestionsString !== newSupport.frequentlyAskedQuestionsString) return true;
  }

  return false;
};

export const checkForTribeChanges = (oldTribe, newTribe) => {
  if (!oldTribe && newTribe) {
    if (newTribe.approvePosts) return true;
    if (newTribe.approvePhotos) return true;
    if (newTribe.approveComments) return true;
    if (newTribe.hashTags && newTribe.hashTags.length) return true;
  }

  if (oldTribe && newTribe) {
    if (oldTribe.approvePosts !== newTribe.approvePosts) return true;
    if (oldTribe.approvePhotos !== newTribe.approvePhotos) return true;
    if (oldTribe.approveComments !== newTribe.approveComments) return true;
    if (oldTribe.hashTags.length !== newTribe.hashTags.length) return true;

    for (let i = 0; i < oldTribe.hashTags.length; i++) {
      if (oldTribe.hashTags[i].text !== newTribe.hashTags[i].text) return true;
    }
  }

  return false;
};

const checkForWeightingChanges = (accountabilityTemplate, weightings) => {
  if (!accountabilityTemplate) {
    if (weightings.weight) return true;
    if (weightings.cm) return true;
    if (weightings.steps) return true;
    if (weightings.objectives) return true;

    return false;
  }

  const measurementTemplate = accountabilityTemplate.measurementTemplate;
  if (measurementTemplate.weight !== weightings.weight) return true;
  if (measurementTemplate.cm !== weightings.cm) return true;
  if (measurementTemplate.steps !== weightings.steps) return true;
  if (measurementTemplate.objectives !== weightings.objectives) return true;

  return false;
};

const checkForMeasurementChanges = (accountabilityTemplate, measurement) => {
  if (!accountabilityTemplate) {
    if (measurement.measurementFrequency) return true;
    if (measurement.measurementStartDay) return true;
    if (measurement.measurementDiagram) return true;

    return false;
  }

  const templateMeasurement = accountabilityTemplate.measurement;
  if (templateMeasurement.measurementFrequency !== measurement.measurementFrequency) return true;
  if (templateMeasurement.measurementStartDay !== measurement.measurementStartDay) return true;
  if (templateMeasurement.measurementDiagram !== measurement.measurementDiagram) return true;

  return false;
};

const checkForConditionChanges = (accountabilityTemplate, conditions) => {
  if (!accountabilityTemplate) {
    if (conditions.length !== initialConditions.length) return true;

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];
      const initialCondition = initialConditions[i];

      if (condition.optionOne !== initialCondition.optionOne) return true;
      if (condition.optionTwo !== initialCondition.optionTwo) return true;
    }

    return false;
  }

  const templateConditions = accountabilityTemplate.conditions;
  if (templateConditions.length !== conditions.length) return true;

  for (let i = 0; i < conditions.length; i++) {
    const templateCondition = templateConditions[i];
    const condition = conditions[i];

    if (templateCondition.optionOne !== condition.optionOne) return true;
    if (templateCondition.optionTwo !== condition.optionTwo) return true;
  }

  return false;
};

const checkForRecommendedExercisesChanges = (accountabilityTemplate, recommendedExercises) => {
  if (!accountabilityTemplate) {
    if (recommendedExercises.length !== initialRecommendedExercises.length) return true;

    for (let i = 0; i < recommendedExercises.length; i++) {
      const recommendedExercise = recommendedExercises[i];
      const initialRecommendedExercise = initialRecommendedExercises[i];

      if (recommendedExercise.externalReference !== initialRecommendedExercise.externalReference) return true;
      if (recommendedExercise.name !== initialRecommendedExercise.name) return true;
      if (recommendedExercise.measurement !== initialRecommendedExercise.measurement) return true;

      const amounts = recommendedExercise.amounts;
      const initialAmounts = initialRecommendedExercise.amounts;
      if (amounts.length !== initialAmounts.length) return true;

      for (let j = 0; j < amounts.length; j++) {
        const amount = amounts[j];
        const initialAmount = initialAmounts[j];

        if (amount.externalReference !== initialAmount.externalReference) return true;
        if (amount.fromWeek !== initialAmount.fromWeek) return true;
        if (amount.toWeek !== initialAmount.toWeek) return true;
        if (amount.amount !== initialAmount.amount) return true;
      }
    }

    return false;
  }

  const mappedRecommendedExercises = [];
  recommendedExercises.forEach((exercise) => {
    exercise.amounts.forEach((amount) => {
      mappedRecommendedExercises.push({
        externalReference: amount.externalReference,
        name: exercise.name,
        dailyAmount: amount.amount,
        measurement: exercise.measurement,
        fromWeek: amount.fromWeek,
        toWeek: amount.toWeek
      });
    });
  });

  const templateRecommendedExercises = accountabilityTemplate.recommendedExercise;
  if (templateRecommendedExercises.length !== mappedRecommendedExercises.length) return true;

  for (let i = 0; i < templateRecommendedExercises.length; i++) {
    const templateRecommendedExercise = templateRecommendedExercises[i];
    const mappedRecommendedExercise = mappedRecommendedExercises[i];

    if (templateRecommendedExercise.externalReference !== mappedRecommendedExercise.externalReference) return true;
    if (templateRecommendedExercise.name !== mappedRecommendedExercise.name) return true;
    if (templateRecommendedExercise.dailyAmount !== mappedRecommendedExercise.dailyAmount) return true;
    if (templateRecommendedExercise.measurement !== mappedRecommendedExercise.measurement) return true;
    if (templateRecommendedExercise.fromWeek !== mappedRecommendedExercise.fromWeek) return true;
    if (templateRecommendedExercise.toWeek !== mappedRecommendedExercise.toWeek) return true;
  }

  return false;
};

const checkForOptionalExercisesChanges = (accountabilityTemplate, optionalExercises) => {
  if (!accountabilityTemplate) {
    if (optionalExercises.length !== initialOptionalExercises.length) return true;

    for (let i = 0; i < optionalExercises.length; i++) {
      const optionalExercise = optionalExercises[i];
      const initialOptionalExercise = initialOptionalExercises[i];

      if (optionalExercise.externalReference !== initialOptionalExercise.externalReference) return true;
      if (optionalExercise.name !== initialOptionalExercise.name) return true;
      if (optionalExercise.measurement !== initialOptionalExercise.measurement) return true;
    }

    return false;
  }

  const templateOptionalExercises = accountabilityTemplate.optionalExercise;
  if (templateOptionalExercises.length !== optionalExercises.length) {
    if (optionalExercises.length === 1 && templateOptionalExercises.length === 0) {
      const optionalExercise = optionalExercises[0];
      if (optionalExercise.name !== '') return true;
      if (optionalExercise.measurement !== '') return true;
    } else {
      return true;
    }
  } else {
    for (let i = 0; i < optionalExercises.length; i++) {
      const templateOptionalExercise = templateOptionalExercises[i];
      const optionalExercise = optionalExercises[i];

      if (templateOptionalExercise.externalReference !== optionalExercise.externalReference) return true;
      if (templateOptionalExercise.name !== optionalExercise.name) return true;
      if (templateOptionalExercise.measurement !== optionalExercise.measurement) return true;
    }
  }

  return false;
};

const checkForSelfCareActivitiesChanges = (accountabilityTemplate, selfCareActivities) => {
  if (!accountabilityTemplate) {
    if (selfCareActivities.length !== initialSelfCareActivities.length) return true;

    for (let i = 0; i < selfCareActivities.length; i++) {
      const selfCareActivity = selfCareActivities[i];
      const initialSelfCareActivity = initialSelfCareActivities[i];

      if (selfCareActivity.externalReference !== initialSelfCareActivity.externalReference) return true;
      if (selfCareActivity.name !== initialSelfCareActivity.name) return true;
    }

    return false;
  }

  const templateSelfCareActivities = accountabilityTemplate.selfCare;
  if (templateSelfCareActivities.length !== selfCareActivities.length) {
    if (selfCareActivities.length === 1 && templateSelfCareActivities.length === 0) {
      if (selfCareActivities[0].name !== '') return true;
    } else {
      return true;
    }
  } else {
    for (let i = 0; i < selfCareActivities.length; i++) {
      const templateSelfCareActivity = templateSelfCareActivities[i];
      const selfCareActivity = selfCareActivities[i];

      if (templateSelfCareActivity.externalReference !== selfCareActivity.externalReference) return true;
      if (templateSelfCareActivity.name !== selfCareActivity.name) return true;
    }
  }

  return false;
};

const checkForSessionChanges = (accountabilityTemplate, sessions) => {
  if (!accountabilityTemplate) {
    return sessions.length > 0;
  }

  const templateSessions = accountabilityTemplate.sessions;
  if (templateSessions.length !== sessions.length) return true;

  for (let i = 0; i < sessions.length; i++) {
    const templateSession = templateSessions[i];
    const session = sessions[i];

    if (templateSession.externalReference !== session.externalReference) return true;
    if (templateSession.name !== session.name) return true;
    if (templateSession.startDate !== session.startDate) return true;
    if (templateSession.startTime !== session.startTime) return true;
    if (templateSession.endDate !== session.endDate) return true;
    if (templateSession.endTime !== session.endTime) return true;
    if (templateSession.reoccurrencePattern !== session.reoccurrencePattern) return true;
  }

  return false;
};

const checkForCharityChanges = (accountabilityTemplate, charityActivities) => {
  if (!accountabilityTemplate) {
    if (charityActivities.length !== initialCharityActivities.length) return true;

    for (let i = 0; i < charityActivities.length; i++) {
      const charityActivity = charityActivities[i];
      const initialCharityActivity = initialCharityActivities[i];

      if (charityActivity.externalReference !== initialCharityActivity.externalReference) return true;
      if (charityActivity.name !== initialCharityActivity.name) return true;
      if (charityActivity.measurement !== initialCharityActivity.measurement) return true;

      const amounts = charityActivity.amounts;
      const initialAmounts = initialCharityActivity.amounts;
      if (amounts.length !== initialAmounts.length) return true;

      for (let j = 0; j < amounts.length; j++) {
        const amount = amounts[j];
        const initialAmount = initialAmounts[j];

        if (amount.externalReference !== initialAmount.externalReference) return true;
        if (amount.fromWeek !== initialAmount.fromWeek) return true;
        if (amount.toWeek !== initialAmount.toWeek) return true;
        if (amount.amount !== initialAmount.amount) return true;
      }
    }

    return false;
  }

  const mappedCharityActivities = [];
  charityActivities.forEach((charityActivity) => {
    charityActivity.amounts.forEach((amount) => {
      mappedCharityActivities.push({
        externalReference: amount.externalReference,
        name: charityActivity.name,
        dailyAmount: amount.amount,
        measurement: charityActivity.measurement,
        fromWeek: amount.fromWeek,
        toWeek: amount.toWeek
      });
    });
  });

  const { charityTemplates } = accountabilityTemplate;
  if (charityTemplates.length !== mappedCharityActivities.length) return true;

  for (let i = 0; i < charityTemplates.length; i++) {
    const charityTemplate = charityTemplates[i];
    const mappedCharityActivity = mappedCharityActivities[i];

    if (charityTemplate.externalReference !== mappedCharityActivity.externalReference) return true;
    if (charityTemplate.name !== mappedCharityActivity.name) return true;
    if (charityTemplate.dailyAmount !== mappedCharityActivity.dailyAmount) return true;
    if (charityTemplate.measurement !== mappedCharityActivity.measurement) return true;
    if (charityTemplate.fromWeek !== mappedCharityActivity.fromWeek) return true;
    if (charityTemplate.toWeek !== mappedCharityActivity.toWeek) return true;
  }

  return false;
};
